import type { ConfigColumns } from 'datatables.net';

declare global {
	interface JQuery {
		/** Append/click a hidden button to a form to force a form submit */
		triggerSubmit(): $;

		/** Disable click events and create a spinner inside an element */
		spinner(options?: { color?: string; backdrop?: string; size?: string }): $;

		/** Remove any spinners inside an element */
		removeSpinner(): $;

		// val() typescript constantly gives grief about the val being potentially string[] | string | number | undefined, so it is being disabled for now
		val(): any;

		/** Scroll bar above and below element */
		doubleScroll(this: $, userOptions?: doubleScrollParams): $;

		/** 3rd party library missing TypeScript definitions */
		datetimepicker(cmd?: any, options?: any): $,

		// Legacy support, not recommended
		serializeDOM(): any;
		serializeObject(): Record<string, any>;
		fill<Type extends Record<string, any>>(data: Type, call?: (name: string, data: Type) => void): $;
		prefix(attr: string | string[] | Record<string, any>, suffix: string): $;
		removePrefix(attr: string, suffix: string): $;
	}
}

if ($.fn.dataTable) {
	$.fn.dataTable.Buttons.defaults.dom!.button!.className = '';
	$.fn.dataTable.defaults.ajax = {
		type: 'POST',
	};
	$.fn.dataTable.defaults.buttons = [];
	$.fn.dataTable.defaults.layout = {
		topStart: 'pageLength',
		topEnd: ['search', 'buttons'],
		bottomStart: 'info',
		bottomEnd: 'paging',
	};
	$.fn.dataTable.defaults.lengthMenu = [
		[10, 25, 50, 100, -1],
		[10, 25, 50, 100, 'All'],
	];
	$.fn.dataTable.defaults.processing = true;
	$.fn.dataTable.defaults.serverSide = true;
}

jQuery.fn.triggerSubmit = function () {
	const $self = $(this);
	const $submit = $('<button type="submit" style="display: none" />');
	$submit.appendTo($self).trigger('click').remove();
	return $self;
};

jQuery.fn.spinner = function ({ color = 'white', size = 'sm' } = {}) {
	if ($(this).children('.spinner-backdrop').length) return $(this);
	return $(this).prop('disabled', true).css('pointer-events', 'none').addClass('position-relative').prepend(`
		<div class="spinner-backdrop position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center z-300">
			<span class="spinner-border spinner-border-${size} text-${color}"></span>
		</div>
	`);
};

jQuery.fn.removeSpinner = function () {
	$(this).prop('disabled', false).css('pointer-events', 'auto').removeClass('position-relative');
	$(this).children('.spinner-backdrop').remove();
	return $(this);
};

type doubleScrollParams = {
	$insertBefore?: $;
	contentElement?: $;
	scrollCss?: any;
	contentCss?: any;
	onlyIfScroll?: boolean;
	resetOnWindowResize?: boolean;
	timeToWaitForResize?: number;
};
jQuery.fn.doubleScroll = function (userOptions) {
	//Merge user options with default options
	const options: doubleScrollParams = {
		$insertBefore: undefined, // Insert second scroll bar before this element, if not specified main element will be used

		contentElement: undefined, // Widest element, if not specified first child element will be used
		scrollCss: {
			'overflow-x': 'auto',
			'overflow-y': 'hidden',
			height: '20px',
		},
		contentCss: {
			'overflow-x': 'auto',
			'overflow-y': 'hidden',
		},
		onlyIfScroll: true, // top scrollbar is not shown if the bottom one is not present
		resetOnWindowResize: true, // recompute the top ScrollBar requirements when the window is resized
		timeToWaitForResize: 30, // wait for the last update event (usefull when browser fire resize event constantly during ressing)

		...userOptions,
	};

	const internalOptions = {
		topScrollBarMarkup: '<div class="doubleScroll-scroll-wrapper"><div class="doubleScroll-scroll"></div></div>',
		topScrollBarInnerSelector: '.doubleScroll-scroll',
	};

	const showScrollBar = ($self: $, $topScrollBar: $ | null = null) => {
		// add div that will act as an upper scroll only if not already added to the DOM
		if (!$topScrollBar) {
			const $insertBefore = options.$insertBefore || $self;

			// creating the scrollbar
			// added before in the DOM
			$topScrollBar = $(internalOptions.topScrollBarMarkup);
			$insertBefore.before($topScrollBar);

			// apply the css
			$topScrollBar.css(options.scrollCss);
			$(internalOptions.topScrollBarInnerSelector).css({ height: '20px' });
			$self.css(options.contentCss);

			let scrolling = false;

			// bind upper scroll to bottom scroll
			$topScrollBar.on('scroll.doubleScroll', () => {
				if (scrolling) {
					scrolling = false;
					return;
				}
				scrolling = true;
				$self.scrollLeft($topScrollBar!.scrollLeft() || 0);
			});

			// bind bottom scroll to upper scroll
			$self.on('scroll.doubleScroll', () => {
				if (scrolling) {
					scrolling = false;
					return;
				}
				scrolling = true;
				$topScrollBar!.scrollLeft($self.scrollLeft() || 0);
			});
		}

		const hideScroll = options.onlyIfScroll && $self.get(0)!.scrollWidth <= Math.round($self.width() || 0);
		if (hideScroll) $topScrollBar.hide();
		else $topScrollBar.show();

		// find the content element (should be the widest one)
		let $contentElement: $;
		if (options.contentElement !== undefined && $self.find(options.contentElement).length !== 0) {
			$contentElement = $self.find(options.contentElement);
		} else {
			$contentElement = $self.find('>:first-child');
		}

		// set the width of the wrappers
		$topScrollBar.add($topScrollBar.find(internalOptions.topScrollBarInnerSelector)).width($contentElement.outerWidth() || 0);
		$topScrollBar.width($self.width() || 0);
		$topScrollBar.scrollLeft($self.scrollLeft() || 0);

		return $topScrollBar;
	};

	return this.each((_, element) => {
		const $self = $(element);

		const $topScrollBar = showScrollBar($self);

		// bind the resize handler
		// do it once
		if (options.resetOnWindowResize) {
			let timeout: ReturnType<typeof setTimeout> | null = null;
			$(window).on('resize.doubleScroll', () => {
				// adding/removing/replacing the scrollbar might resize the window
				// so the resizing flag will avoid the infinite loop here...
				if (timeout) clearTimeout(timeout);
				timeout = setTimeout(() => showScrollBar($self, $topScrollBar), options.timeToWaitForResize);
			});
		}
	});
};

const notObj = (obj: any) => typeof obj !== 'object' || obj === null || Array.isArray(obj);
const merge = (objA: any, objB: any) => {
	if (notObj(objA) || notObj(objB) || objA.jquery || objB.jquery) return objB;
	for (let key in objB) {
		if (objA[key] === undefined) objA[key] = objB[key];
		else objA[key] = merge(objA[key], objB[key]);
	}
	return objA;
};

jQuery.fn.serializeDOM = function () {
	const $elements = $(this);
	let DOM: any = {};

	$elements.each((index, element) => {
		const $self = $(element);

		if ($self.is('[data-name]')) {
			const name = $self.data('name');
			DOM[`$${name}`] = $self;
		}

		if ($self.is('[data-section]')) {
			const section = $self.data('section');
			DOM[section] = merge(DOM[section], $self.children().serializeDOM());
			DOM[section].serializeObject = () => $self.serializeObject();
			DOM[section].fill = (...args: any[]) => $self.fill(args[0], args[1]);
		} else if ($self.find('[data-name]').length || $self.find('[data-section]').length) {
			DOM = merge(DOM, $self.children().serializeDOM());
		}
	});
	return DOM;
};

jQuery.fn.serializeObject = function () {
	const $self = $(this);
	const obj: Record<string, ReturnType<typeof jQuery.fn.val>> = {};
	$self
		.find('[data-name]')
		.addBack('[data-name]')
		.each((_, element) => {
			const $element = $(element);
			if (!$element.is('input, textarea, select')) return;

			const key = $element.data('name').toString();
			obj[key] = $element.val();
		});
	return obj;
};

jQuery.fn.fill = function (data, call) {
	const $form = $(this);
	$form
		.find('[data-name]')
		.addBack('[data-name]')
		.each((_, element) => {
			const $item = $(element);
			const name = $item.data('name').toString();
			let val = call instanceof Function ? call(name, data) : data[name];
			if (Array.isArray(val)) val = val.join(', ');
			$item.val(val);
		});
	return $form;
};

jQuery.fn.prefix = function (attr, prefix) {
	const $element = $(this);

	if (Array.isArray(attr)) {
		attr.forEach((a) => $element.prefix(a, prefix));
		return $element;
	} else if (typeof attr === 'object') {
		for (const [a, prefix] of Object.entries(attr)) {
			$element.prefix(a, prefix);
		}
		return $element;
	}

	const addPrefix = (element: $) => {
		if (element.children().length > 0) {
			element.children().each((_, child) => addPrefix($(child)));
		}
		const str = element.attr(attr);
		if (str !== undefined && str.startsWith(prefix)) {
			element.attr(attr, prefix + str);
		}
	};

	return $element;
};

jQuery.fn.removePrefix = function (attr, prefix) {
	const $element = $(this[0]);

	const remotePrefix = (element) => {
		if (element.children().length > 0) {
			element.children().each((_, child) => remotePrefix($(child)));
		}
		const str = element.attr(attr);
		if (str !== undefined && str.startsWith(prefix)) element.attr(attr, str.substring(prefix.length));
	};

	remotePrefix($element);

	return $element;
};

/** Set timeout as a promise */
export const setDelay = (ms: number) => new Promise<void>((resolve) => setTimeout(() => resolve(), ms));

/** Queues bootstrap notification to top right of screen */
export const displayNotification = (msg: string, background: BootstrapFlavor = 'success', time = 2000, icon = '') => {
	function generateNotificationID(length) {
		const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
		let result = '';
		for (let i = 0; i < length; i++) {
			result += characters.charAt(Math.floor(Math.random() * characters.length));
		}
		return result;
	}
	let notificationID = generateNotificationID(8); 

	const $notifications = $('.notifications').length ? $('.notifications') : $(`
		<div class="notifications position-fixed top-5 end-5 z-1100 d-flex flex-column flex-nowrap"></div>`).prependTo($('body'));
	const $notification = $(`
		<div id='${notificationID}' class="notification alert-${background}" style="border-radius: 10px; margin-bottom: 10px;">
			<div class="alert alert-dismissible" role="alert" style="margin-bottom: 0">
				<span>${(icon != '' ? '<i class="fas fa-' + icon + ' fa-lg me-2"></i>' : '')}${msg}</span>
				<button value=${notificationID} type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
			</div>
			<div id="progress-${notificationID}" class="progress bg-${background}" style="width: 97%; height: 4px; margin: 0 auto;">
				<div id="progress-bar-${notificationID}" class="progress-bar bg-dark" role="progressbar" aria-valuenow="100" aria-valuemin="0" aria-valuemax="100" style="width: 75%; opacity: 0.3;"></div>
			</div>
		</div>
	`);
	$(function () {
		$('.btn-close').on('click', function () {
		  const notificationID = $(this).attr('value');
		  const progressBarId = `progress-${notificationID}`;
		  $('#' + progressBarId).remove();
		});
	});
	$notification.appendTo($notifications);
	let remainingTime = time;

	const updateProgressBar = () => {
		const progress = (time - remainingTime) / time * 100;
		$('#progress-bar-' + notificationID).css('width', progress + '%');
		remainingTime -= 100;
		if (remainingTime > -500) {
			setTimeout(updateProgressBar, 100);
		} else {
			$('#' + notificationID).fadeOut(500, () => {
				$('#' + notificationID).remove();
				$('#progress-' + notificationID).remove();
			});
		}
	};

	updateProgressBar();
};

export const arraysEqual = (a: any[], b: any[]) => {
	if (a === b) return true;
	if (a == null || b == null) return false;
	if (a.length !== b.length) return false;

	return a.find((val, index) => val !== b[index]) === undefined;
};

/** Show bootstrap confirmation modal with a message. Callback when 'Confirm' modal footer button is clicked. */
export const confirmModal = (msg: string, callback: () => void) => {
	const $modal = $(`
		<div class="modal fade" tabindex="-1" role="dialog">
			<div class="modal-dialog" role="document">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title">Confirm</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body">
						<p>${msg}</p>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
						<button type="button" class="confirm btn btn-primary" data-bs-dismiss="modal">Confirm</button>
					</div>
				</div>
			</div>
		</div>
	`);
	$modal.find('.confirm').on('click', () => callback());
	$modal.on('hidden.bs.modal', () => $modal.remove());
	$modal.appendTo('body').modal('show');
};

type confirmModalPromiseParams = {
	msg: string;
	title?: string;
};
/** Show bootstrap confirmation modal with a message. Return promise with confirmation status. */
export const confirmModalPromise = ({msg, title = 'Confirm'}: confirmModalPromiseParams) => new Promise<boolean>((resolve) => {
	const $modal = $(`
		<div class="modal fade" tabindex="-1" role="dialog">
			<div class="modal-dialog" role="document">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title">${title}</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body">
						<p>${msg}</p>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
						<button type="button" class="confirm btn btn-primary">Confirm</button>
					</div>
				</div>
			</div>
		</div>
	`);
	const $confirm = $modal.find('.confirm');

	$confirm.on('click', () => {
		resolve(true);
		$modal.modal('hide');
	});

	$modal.on('hide.bs.modal', () => resolve(false));
	$modal.on('hidden.bs.modal', () => $modal.remove());
	$modal.appendTo('body').modal('show');
});

export const cronValidate = (arr: number[]) => {
	if (arr.length != 5) return false;

	const ret = [true, true, true, true, true];

	// Minute
	if (isNaN(arr[0]) || arr[0] < 0 || arr[0] > 59) ret[0] = false;

	// Hour
	if (!/^[0-9,\-*]+$/.test(arr[1].toString()) || arr[1] < 0 || arr[1] > 23) ret[1] = false;

	// Day
	if (!/^[0-9,\-*]+$/.test(arr[2].toString()) || arr[2] < 1 || arr[2] > 31) ret[2] = false;

	// Month
	if (!/^[0-9,\-*]+$/.test(arr[3].toString()) || arr[3] < 1 || arr[3] > 12) ret[3] = false;

	// Weekday
	if (!/^[0-9,\-*]+$/.test(arr[4].toString()) || arr[4] < 0 || arr[4] > 6) ret[4] = false;

	return ret;
};

/** Object deep clone */
export const clone = <T>(obj: T): T => {
	if (obj == null || typeof(obj) != 'object') return obj;

	const temp = new (obj as any).constructor() as T; 
	for (const key in obj) temp[key] = clone(obj[key]);

	return temp;
};

/** Populate a form with data */
export const setForm = ({ form, setData }: { form: DataForm; setData?: Record<string, any> }) => {
	form.forEach(({ $element, dataSrc }) => {
		const val = setData && setData.hasOwnProperty(dataSrc) ? setData[dataSrc] : '';

		if ($element.get(0)?.isContentEditable) $element.text(val);
		else $element.val(val);

		if ($element.is('.chosen')) $element.trigger('chosen:updated');
		if ($element.is('.selectpicker')) $element.selectpicker('refresh');
	});
};

/** Get form data */
export const getForm = ({ form }: { form: DataForm }): Record<string, any> => form.reduce(
	(data, { $element, dataSrc }) => ({
		...data,
		[dataSrc]: $element.val(),
	}),
	{}
);

/** Make a btn the collapse trigger for a target.  Fontawesome icons inside the button of class 'fa-caret-up'/'fa-caret-down' will be automatically toggled as well. */
export const setCollapse = ($btn: $, $target: $, collapsed?: boolean) => {
	const show = () => {
		$target.slideDown(350);
		$btn.find('i').addClass('fa-caret-up').removeClass('fa-caret-down');
	};
	const hide = () => {
		$target.slideUp(350);
		$btn.find('i').addClass('fa-caret-down').removeClass('fa-caret-up');
	};
	$btn.off('click').on('click', () => {
		if ($target.css('display') === 'none') show();
		else hide();
	});
	if (collapsed !== undefined) {
		if (collapsed) hide();
		else show();
	}
};

interface inputNumParams {
	$input: $,
	min?: number | null,
	max?: number | null,
	integer?: boolean,
	step?: number | null,
	decimals?: number | null,
	defaultVal?: string | number,
	callback?: (val: string | number) => void,
};
export const inputNum = ({ $input, min = null, max = null, integer = false, step = null, decimals = null, defaultVal = '', callback }: inputNumParams) => {
	if (!$input || !$input.length) return;
	if (step !== null) $input.attr('step', step);
	if (min !== null) $input.attr('min', min);
	if (max !== null) $input.attr('max', max);
	$input.on('change', () => {
		let newNum = $input.val();
		if (isNaN(newNum)) {
			$input.val(defaultVal);
			if (callback) callback(defaultVal);
			return;
		}
		newNum = +newNum;
		if (decimals !== null) newNum = newNum.toFixed(decimals);
		if (min !== null && newNum < min) newNum = min;
		if (max !== null && newNum > max) newNum = max;
		if (integer) newNum = Math.floor(newNum);
		$input.val(newNum);
		if (callback) callback(newNum);
	});
};

let id = 1;
export const getId = () => id++;

/** Video.js simple api wrapper */
export const videoJsPlayer = async ({ $cont, url, autoplay = false }: { $cont: $; url: string; autoplay: boolean }) => {
	const id = `video-${getId()}`;

	const $video = $(`
		<div>
			<video id="${id}" class="video video-js vjs-16-9 border border-primary rounded">
				<source type="video/mp4" src="${url}">
			</video>
			<button class="video-fullscreen btn btn-primary rounded-0 rounded-bottom w-100"><i class="fas fa-expand me-2"></i>Fullscreen</button>
		</div>
	`).appendTo($cont);

	const { default: videojs } = await import('video.js/dist/alt/video.core.novtt.js');
	const player = videojs(id, {
		controls: true,
		autoplay,
	});

	player.ready(() => {
		const $fullscreenIcon = $video.find('.vjs-fullscreen-control');
		const $fullscreenBtn = $video.find('.video-fullscreen');

		$fullscreenBtn.on('click', () => $fullscreenIcon.trigger('click'));

		player.on('ended', () => {
			if (player.isFullscreen()) $fullscreenIcon.trigger('click');
		});
	});

	const pause = () => player.pause();
	const play = () => player.play();
	const stop = () => {
		player.dispose();
		$video.remove();
	};

	return { pause, play, stop };
};

/** Post to the provided URL with the specified parameters */
export const formPost = (path: string, parameters?: object | null, target?: string) => {
	const $form = $('<form></form>');

	$form.attr('method', 'post');
	$form.attr('action', path);
	if (target !== undefined) $form.attr('target', target);

	if (parameters !== null && parameters !== undefined) {
		for (const [key, value] of Object.entries(parameters)) {
			const $input = $('<input></input>');

			$input.attr('type', 'hidden');
			$input.attr('name', key);
			$input.attr('value', value);

			$form.append($input);
		}
	}

	// The form needs to be a part of the document in
	// order for us to be able to submit it.
	$(document.body).append($form);
	$form.trigger('submit');
};

export const penniesString = (pennies: number) => {
	let str = (Math.abs(pennies) / 100).toFixed(2).toString();
	str = str.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
	str = '$' + str;
	if (pennies < 0) str = `(${str})`;
	return str;
};

export const minutesToMilliseconds = (minutes: number) => minutes * 60 * 1000;

export const validCurrency = (amount, {negatives = false, pennies = false, zero = true} = {}) => {
	if (amount === '') return null;
	amount = +amount;
	if (isNaN(amount) || (!negatives && amount < 0) || (!zero && amount === 0)) return null;

	const decimalsCount = amount.toString().split('.')[1]?.length;
	if (decimalsCount > 2) amount = amount.toFixed(2);
	if (pennies) amount *= 100;
	return amount;
};

export const superadminCheck = (res) => {
	if (res.hasOwnProperty('rc') && res.rc === 'NO_AUTH' && res.hasOwnProperty('redirect')) {
		window.location.replace(res.redirect);
		return false;
	}

	return true;
};

/** Fetch post wrapper that works with PHP $_POST and throws responses that don't have {rc: 'OK'} */
export const fetchPost = async (url: string, postData?: object | FormData, options = { returnJSON: true }) => {
	let requestBody: RequestInit['body'] = null;
	const headers: RequestInit['headers'] = options.returnJSON ?  {
		Accept: 'application/json',
	} : {};
	if (postData instanceof FormData) {
		requestBody = postData;
	} else if (postData !== undefined) {
		requestBody = JSON.stringify(postData);
		headers['Content-Type'] = 'application/json';
	}
	const response = await fetch(url, {
		method: 'POST',
		headers,
		body: requestBody,
	});
	if(!options.returnJSON) return response.body;
	const json = await response.json();
	superadminCheck(json);
	if (json.hasOwnProperty('rc') && json.rc !== 'OK') throw json;
	return json;
};

/** Get row data from datatables via the tr element or any element inside the tr */
export const getTableRowData = (table: DataTable, element: HTMLElement): any => table.row($(element).closest('tr')).data();

export const dtColumnIndicies = (columns: ConfigColumns[]) => columns.reduce((indicies, col, index) => ({ ...indicies, [col.data as string]: index }), {}) as Record<string, number>;

export const datatableDataSrc = (table: DataTable, data: any, extraFields: string[] = []) => {
	if (!Array.isArray(data.data[0])) return data.data;

	const keys = table.columns().dataSrc().toArray();
	const rows = data.data.map((values) => {
		let row = {};
		keys.forEach((key, index) => {
			if (key) row[key] = values[index];
		});
		extraFields.forEach((field, index) => {
			row[field] = values[index + keys.length];
		});
		return row;
	});
	return rows;
};

/** Datatables table.ajax.reload as a promise */
export const tableReload = (table: DataTable) => new Promise<void>((resolve) => {
	if (!table) resolve();
	else table.ajax.reload(resolve, false)
});

export const datatableDoubleScroll = ($table: $) => {
	const $scroll = $table.find('.dt-scroll');
	$scroll.find('.dt-scroll-body').doubleScroll({ $insertBefore: $scroll });
	window.dispatchEvent(new Event('resize'));
};

export const capitalizeFirstLetter = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

export const createAudioPlayer = () => {
	const audio = new Audio();
	const $audio = $(audio);
	let baseURL = '';
	let stopped = true;

	const play = (url?: string, delay: number = 0) => {
		stop();
		stopped = false;

		const startTime = new Date().getMilliseconds();
		$audio.one('canplaythrough', () => {
			$audio.off('canplaythrough');
			const millisecondsSinceStart = Math.abs(startTime - new Date().getMilliseconds());
			const startDelay = delay - millisecondsSinceStart;
			setTimeout(() => audio.play(), startDelay > 0 ? startDelay : 0);
		});

		$audio.on('ended', () => stop());

		const fullURL = `${baseURL}${url}`;
		audio.src = fullURL;
		audio.load();
	};

	const base = (url: string) => {
		baseURL = url;
	};

	const stop = () => {
		$audio.off();
		audio.pause();
		stopped = true;
	};

	const pause = () => audio.pause();

	const resume = () => {
		if (stopped) return;
		audio.play();
	};

	const volume = (percent: number) => (audio.volume = percent / 100);

	return {
		play,
		stop,
		pause,
		resume,
		base,
		volume,
		$audio,
	};
};

export const textEditor = ({ language, title, headerHtml, text }: { language: string; title: string; headerHtml?: string, text: string }) => new Promise<string>(async (resolve) => {
	if (!$('#editorModal').length) {
		$('body').append(`
			<div id="editorModal" data-name="modal" class="modal fade" tabindex="-1">
				<div class="modal-dialog modal-dialog-centered modal-xl">
					<div class="modal-content">
						<div class="modal-header">
							<h5 id="editorModalTitle" class="modal-title"></h5>
							<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
						</div>
						<div class="modal-body">
							<div id="editorHeader" class="mb-3"></div>
							<div id="aceEditor" style="width: 100%; height: 80vh;"></div>
							<div id="editorModalSpinner">
								<div class="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center" style="pointer-events: none;">
									<div class="spinner-border" role="status">
										<span class="visually-hidden">Loading...</span>
									</div>
								</div>
							</div>
						</div>
						<div class="modal-footer">
							<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
							<button type="button" id="editorModalUpdate" class="btn btn-primary">Update</button>
						</div>
					</div>
				</div>
			</div>
		`);
	}

	$('#editorModalSpinner').show();
	$('#editorModal').modal('show');
	const ace = await import('brace');
	await Promise.all([import('brace/ext/language_tools'), import(`brace/snippets/${language}`), import(`brace/mode/${language}`), import('brace/theme/tomorrow_night')]);

	const editor = ace.edit('aceEditor');
	editor.setTheme('ace/theme/tomorrow_night');
	editor.session.setMode(`ace/mode/${language}`);
	editor.session.setUseWrapMode(true);
	editor.setOptions({
		enableBasicAutocompletion: true,
		enableSnippets: true,
		enableLiveAutocompletion: true,
	});
	if (language === 'json') {
		text = JSON.stringify(JSON.parse(text), null, '\t');
	}
	editor.setValue(text);

	$('#editorModalSpinner').hide();

	if (headerHtml) $('#editorHeader').show().html(headerHtml);
	else $('#editorHeader').hide();

	$('#editorModalTitle').html(title);
	$('#editorModalUpdate').off('click').on('click', () => {
		const value = editor.getValue();
		resolve(value);
		$('#editorModal').modal('hide');
	});
});

export const logError = (error) => {
	try {
		if ((window as any).isDev) console.error(error);
		const postData = {
			location: window.location.href,
			useragent: navigator.userAgent,
			stack: new Error().stack,
			error,
		};
		fetchPost('/data/logerror', postData);
	} catch (anotherError) {
		console.error('ERRORCEPTION?!');
		console.error(anotherError);
	}
};
